<script setup lang="ts">
	import {
		ArcElement,
		Chart as ChartJS,
		Legend,
		Title,
		Tooltip,
	} from 'chart.js'
	import { tooltip } from 'leaflet'
	import { computed } from 'vue'
	import { Pie } from 'vue-chartjs'

	import useCompanyReportHotelDestinations from '@/modules/companies/composables/useCompanyReportHotelDestinations'

	interface Props {
		companyId?: number
		dateRange?: string[] | null
		creationDateRange?: string[] | null
		statusFilter?: string
		groupByFilter?: string
		searchTravelerId?: number | null
		searchHotelId?: number | null
		createdAt?: string[] | null
		orderBy?: string
		orderDirection?: 'asc' | 'desc'
	}

	const props = defineProps<Props>()

	// Hacer petición autónoma
	const { destinations, isFetching: isLoading } =
		useCompanyReportHotelDestinations(
			computed(() => ({
				companyId: props.companyId,
				filters: {
					dates: props.dateRange,
					creationDates: props.creationDateRange,
					status: props.statusFilter !== 'all' ? props.statusFilter : undefined,
					groupBy: props.groupByFilter,
					travelerId: props.searchTravelerId || undefined,
					hotelId: props.searchHotelId || undefined,
					createdAt: props.createdAt || undefined,
					orderBy: props.orderBy || undefined,
					orderDirection: props.orderDirection || undefined,
				},
			}))
		)

	ChartJS.register(Title, Tooltip, Legend, ArcElement)

	const chartData = computed(() => {
		if (!destinations.value || destinations.value.length === 0) return null

		const hasValidData = destinations.value.some((d: any) => d.reservations > 0)
		if (!hasValidData) return null

		// Tomar top 10 hoteles
		const topHotels = destinations.value.slice(0, 10)

		return {
			labels: topHotels.map((d) => d.hotelName),
			datasets: [
				{
					label: 'Reservas',
					data: topHotels.map((d) => d.percentage),
					backgroundColor: [
						'rgba(147, 51, 234, 0.8)',
						'rgba(59, 130, 246, 0.8)',
						'rgba(16, 185, 129, 0.8)',
						'rgba(245, 158, 11, 0.8)',
						'rgba(239, 68, 68, 0.8)',
						'rgba(236, 72, 153, 0.8)',
						'rgba(14, 165, 233, 0.8)',
						'rgba(34, 197, 94, 0.8)',
						'rgba(168, 85, 247, 0.8)',
						'rgba(251, 146, 60, 0.8)',
					],
				},
			],
		}
	})

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: 'right' as const,
				labels: {
					boxWidth: 12,
					font: {
						size: 11,
					},
				},
			},
			tooltip: {
				callbacks: {
					label: function (context: any) {
						if (!destinations.value) return ''
						const hotel = destinations.value[context.dataIndex]

						return [
							`Reservas:        ${hotel.reservations}`,
							`Porcentaje:      ${hotel.percentage.toFixed(1)}%`,
							`Ciudad:          ${hotel.city}`,
							`Cuartos noche:   ${hotel.roomNights}`,
						]
					},
				},
			},
			title: {
				display: true,
				text: 'Top 10 Hoteles Más Utilizados',
			},
			datalabels: {
				display: true,
				color: '#fff',
				font: {
					weight: 'bold' as const,
					size: 12,
				},
				formatter: (value: number) => {
					if (value === 0) return ''
					return `${value.toFixed(1)}%`
				},
			},
		},
	}

	// Exponer isLoading para el componente padre
	defineExpose({
		isLoading,
	})
</script>

<template>
	<div class="overflow-hidden rounded-lg bg-white p-6 shadow">
		<div
			v-if="isLoading && !chartData"
			class="flex items-center justify-center"
			style="height: 300px"
		>
			<div class="text-center">
				<span
					class="material-symbols-outlined mb-2 animate-spin text-4xl text-blue-ef-500"
				>
					progress_activity
				</span>
				<p class="text-sm text-gray-600">Cargando hoteles...</p>
			</div>
		</div>

		<div
			v-else-if="chartData"
			style="height: 300px"
		>
			<Pie
				:data="chartData"
				:options="chartOptions"
			/>
		</div>

		<div
			v-else
			class="flex items-center justify-center"
			style="height: 300px"
		>
			<div class="text-center">
				<span class="material-symbols-outlined mb-4 text-6xl text-gray-300">
					hotel
				</span>
				<p class="text-base font-medium text-gray-600">
					No hay datos disponibles para esta sección
				</p>
				<p class="mt-2 text-sm text-gray-500">
					No se encontraron hoteles en el período seleccionado
				</p>
			</div>
		</div>
	</div>
</template>
